import { useCallback, useState } from "react";

export interface UseSelectionOptions {
  /** IDs of items on the current page */
  pageItemIds: string[];
  /** IDs of items that should not be selectable (e.g., processing documents) */
  disabledIds?: Set<string>;
}

export interface UseSelectionReturn {
  /** Currently selected item IDs */
  selectedIds: Set<string>;
  /** Whether all items on current page are selected */
  isAllPageSelected: boolean;
  /** Whether all filtered items are selected */
  isAllFilteredSelected: boolean;
  /** Last selected item ID (for shift-click range selection) */
  lastSelectedId: string | null;
  /** Toggle a single item selection */
  toggleSelection: (id: string) => void;
  /** Toggle all items on current page */
  toggleAllOnPage: () => void;
  /** Select all items matching current filters */
  selectAllFiltered: (allFilteredIds: string[]) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Handle row click with modifier key support */
  handleRowClick: (id: string, event: React.MouseEvent) => void;
  /** Get selection count */
  selectionCount: number;
}

/**
 * Hook to manage document selection state with support for
 * multi-select, shift-click range selection, and select all
 */
export function useSelection(options: UseSelectionOptions): UseSelectionReturn {
  const { pageItemIds, disabledIds = new Set() } = options;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [allFilteredSelected, setAllFilteredSelected] = useState(false);

  // Filter out disabled items from page items
  const selectablePageIds = pageItemIds.filter((id) => !disabledIds.has(id));

  // Check if all selectable items on current page are selected
  const isAllPageSelected =
    selectablePageIds.length > 0 && selectablePageIds.every((id) => selectedIds.has(id));

  const isAllFilteredSelected = allFilteredSelected;

  const selectionCount = selectedIds.size;

  const toggleSelection = useCallback(
    (id: string) => {
      if (disabledIds.has(id)) return;

      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      setLastSelectedId(id);
      setAllFilteredSelected(false);
    },
    [disabledIds]
  );

  const toggleAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllPageSelected) {
        // Deselect all page items
        selectablePageIds.forEach((id) => next.delete(id));
      } else {
        // Select all page items
        selectablePageIds.forEach((id) => next.add(id));
      }
      return next;
    });
    setAllFilteredSelected(false);
  }, [isAllPageSelected, selectablePageIds]);

  const selectAllFiltered = useCallback(
    (allFilteredIds: string[]) => {
      const selectableIds = allFilteredIds.filter((id) => !disabledIds.has(id));
      setSelectedIds(new Set(selectableIds));
      setAllFilteredSelected(true);
    },
    [disabledIds]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
    setAllFilteredSelected(false);
  }, []);

  const handleRowClick = useCallback(
    (id: string, event: React.MouseEvent) => {
      if (disabledIds.has(id)) return;

      const isCtrlOrCmd = event.ctrlKey || event.metaKey;
      const isShift = event.shiftKey;

      if (isShift && lastSelectedId) {
        // Range selection: select all items between lastSelectedId and current id
        const lastIndex = pageItemIds.indexOf(lastSelectedId);
        const currentIndex = pageItemIds.indexOf(id);

        if (lastIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(lastIndex, currentIndex);
          const end = Math.max(lastIndex, currentIndex);
          const rangeIds = pageItemIds.slice(start, end + 1).filter((id) => !disabledIds.has(id));

          setSelectedIds((prev) => {
            const next = new Set(prev);
            rangeIds.forEach((rangeId) => next.add(rangeId));
            return next;
          });
        }
        setAllFilteredSelected(false);
      } else if (isCtrlOrCmd) {
        // Toggle single item
        toggleSelection(id);
      } else {
        // Plain click: select only this item
        setSelectedIds(new Set([id]));
        setLastSelectedId(id);
        setAllFilteredSelected(false);
      }
    },
    [disabledIds, lastSelectedId, pageItemIds, toggleSelection]
  );

  return {
    selectedIds,
    isAllPageSelected,
    isAllFilteredSelected,
    lastSelectedId,
    toggleSelection,
    toggleAllOnPage,
    selectAllFiltered,
    clearSelection,
    handleRowClick,
    selectionCount,
  };
}
